import { Button, ToastNotifications } from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import React from 'react'
import { datadogLogs } from '@datadog/browser-logs'
import { FEATURE_ENABLE_INVOICING_PAYMENT_REQUESTS } from '../../../../constants'
import { buildRetailerDetails } from '../../../../functions/invoicing/payment-request-resources'
import { validateMerchantAccount } from '../../../../functions/invoicing/validate-merchant-account'
import { isValidCustomer } from '../../../../functions/sale'
import {
  usePaymentManager,
  useRegisterManager,
  useRetailerSettingsManager,
} from '../../../../ng-compat/hooks'
import { useAppSelector } from '../../../../state/hooks'
import {
  selectSaleCustomer,
  selectSaleHasLineItemFulfilment,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import { PaymentRequestModalWrapper } from './PaymentRequestModalWrapper'

type DatadogLogs = typeof datadogLogs
declare let DD_LOGS: DatadogLogs

interface PaymentRequestButtonProps {
  amountTendered: string // is the amount entered for partial payments on the Payment Panel, if not edited then equals the sale outstanding balance
}

export const PaymentRequestButton: React.FC<PaymentRequestButtonProps> = ({
  amountTendered,
}) => {
  const [showPaymentRequestModal, setShowPaymentRequestModal] = React.useState(
    false
  )
  const [isValidatingMerchant, setIsValidatingMerchant] = React.useState(false)
  const [hasLSPay, setHasLSPay] = React.useState(false)

  const t = useT()
  const hasFulfillmentLineItems = useAppSelector(
    selectSaleHasLineItemFulfilment
  )
  const saleCustomer = useAppSelector(selectSaleCustomer)
  const paymentManager = usePaymentManager()
  const registerManager = useRegisterManager()
  const retailer = useRetailerSettingsManager().get()
  const currentOutlet = registerManager.getActiveOutlet()

  React.useEffect(() => {
    const checkHasLSPaymentType = async () => {
      try {
        const hasLSPayPaymentType = await paymentManager.hasLspayPaymentType()
        setHasLSPay(hasLSPayPaymentType)
      } catch {
        setHasLSPay(false)
      }
    }
    checkHasLSPaymentType()
  }, [paymentManager])

  const isFeatureEnabled = getFeatureFromCache(
    FEATURE_ENABLE_INVOICING_PAYMENT_REQUESTS
  )

  const isOutletInUSAOrCanada =
    currentOutlet?.physical_country_id === 'US' ||
    currentOutlet?.physical_country_id === 'CA'

  const showButton =
    isFeatureEnabled &&
    hasFulfillmentLineItems &&
    isOutletInUSAOrCanada &&
    isValidCustomer(saleCustomer) &&
    hasLSPay

  if (!showButton) {
    DD_LOGS.logger.info('payment_request_button_not_displayed', {
      isFeatureEnabled,
      hasFulfillmentLineItems,
      isOutletInUSAOrCanada,
      outletCountry: currentOutlet?.physical_country_id,
      isValidCustomer: isValidCustomer(saleCustomer),
      hasLSPay,
      hasCustomer: !!saleCustomer,
      customerId: saleCustomer?.id,
    })
    return null
  }

  DD_LOGS.logger.info('payment_request_button_displayed', {
    accountId: retailer?.id,
  })

  const retailerDetails = buildRetailerDetails({ retailer })

  const showMerchantValidationError = () => {
    ToastNotifications.negative(
      t(
        'Unable to process payment requests at the moment, merchant validation failed.',
        {
          _context: 'Payment request merchant validation error',
        }
      )
    )
  }

  const handleButtonClick = async () => {
    setIsValidatingMerchant(true)

    try {
      await validateMerchantAccount(retailerDetails)
      setShowPaymentRequestModal(true)
    } catch (error) {
      showMerchantValidationError()
    } finally {
      setIsValidatingMerchant(false)
    }
  }

  return (
    <>
      <div className="vd-mt4">
        <Button
          jumbo
          className="btn--payment-request"
          variant="supplementary"
          onClick={handleButtonClick}
          disabled={isValidatingMerchant}
        >
          <T
            _str={
              isValidatingMerchant
                ? 'Preparing payment request form...'
                : 'Request payment'
            }
            _context="Payment request button label"
          />
        </Button>
      </div>
      <PaymentRequestModalWrapper
        showPaymentRequestModal={showPaymentRequestModal}
        setShowPaymentRequestModal={() => setShowPaymentRequestModal(false)}
        amountTendered={amountTendered}
      />
    </>
  )
}
